import React, {ReactNode} from 'react'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'

import {cssTheme} from '../../../themes'
import {SkeletonLoader} from '../SkeletonLoader'
import {Text} from '../Text'

interface Props {
  /** Pass through classname to allow styles overrides */
  className?: string
  /** The label to be shown next to the badge */
  label?: ReactNode
  /** The sub label shown below the label */
  subLabel?: ReactNode
  /** The number to be shown in the badge */
  value: number
  /** Wether the subLabel goes below or in the same line as the label */
  singleLineLabels?: boolean
  /** The ID of the Score Rating value */
  scoreId?: string
  /** The ID of the Label text i.e. Superb/Good */
  scoreLabelId?: string
  /** The ID of the SubLabel which is review count  */
  scoreSubLabelId?: string
  /** show score rating in a single line without border-radius or background */
  isCompact?: boolean
}

interface ScoreProps {
  id?: string
  value: number
  isCompactWrapper?: boolean
}

const BADGE_SIZE = 32
const SINGLE_LINE_BADGE_SIZE = 24
const BADGE_RADIUS = 20
const BADGE_RADIUS_TAIL = 4

const RatingElement = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${cssTheme.layout.spacing.s300};
  &.isSingleLine {
    gap: ${cssTheme.layout.spacing.s200};
  }
  &.isCompact {
    gap: ${cssTheme.layout.spacing.s100};
  }
`

const BadgeWrapper = styled.div`
  ${cssTheme.typography.labelS}
  min-width: ${BADGE_SIZE}px;
  line-height: ${BADGE_SIZE}px;
  text-align: center;
  border-radius: ${BADGE_RADIUS}px ${BADGE_RADIUS}px ${BADGE_RADIUS_TAIL}px
    ${BADGE_RADIUS}px;
  .isSingleLine & {
    ${cssTheme.typography.labelXS};
    min-width: ${SINGLE_LINE_BADGE_SIZE}px;
    line-height: ${SINGLE_LINE_BADGE_SIZE}px;
  }
  .--poor & {
    background-color: ${cssTheme.colors.scoreRating.default.background.neutral};
    color: ${cssTheme.colors.scoreRating.default.score.neutral};
  }
  .--high & {
    background-color: ${cssTheme.colors.scoreRating.default.background.success};
    color: ${cssTheme.colors.scoreRating.default.score.success};
  }
`

const LabelWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-around;
  gap: ${cssTheme.layout.spacing.s000};
  .isSingleLine & {
    flex-direction: row;
    align-items: center;
    gap: ${cssTheme.layout.spacing.s200};
  }
  .isCompact & {
    flex-direction: row;
    align-items: center;
    gap: ${cssTheme.layout.spacing.s100};
  }
`

const LabelText = styled(Text)`
  display: flex;
  align-items: center;
  color: ${cssTheme.colors.scoreRating.default.description};
`

const SubLabelText = styled(Text)`
  display: flex;
  align-items: center;
  color: ${cssTheme.colors.scoreRating.default.review};
`

export const CompactBadge = styled(Text)`
  .--high & {
    color: ${cssTheme.colors.scoreRating.compact.score.success};
  }
  .--poor & {
    color: ${cssTheme.colors.scoreRating.compact.score.neutral};
  }
`

const Score = ({value, id, isCompactWrapper = false}: ScoreProps) => {
  if (isCompactWrapper) {
    if (!value) {
      return null
    }
    return (
      <CompactBadge id={id} variant="labelXS" align="center">
        {value}
      </CompactBadge>
    )
  }

  return <BadgeWrapper id={id}>{value || '-'}</BadgeWrapper>
}

export const ScoreRatingLoader = ({isCompact}: {isCompact?: boolean}) =>
  isCompact ? (
    <SkeletonLoader height={16} width={74} marginTop={0} />
  ) : (
    <SkeletonLoader height={28} width={28} marginTop={0} />
  )

export const ScoreRating = ({
  label,
  subLabel,
  value,
  className,
  singleLineLabels = false,
  isCompact = false,
  scoreId,
  scoreLabelId,
  scoreSubLabelId
}: Props) => (
  <RatingElement
    className={cx(
      isCompact && 'isCompact',
      singleLineLabels && 'isSingleLine',
      value >= 7 && '--high',
      value < 7 && '--poor',
      className
    )}
  >
    <Score id={scoreId} value={value} isCompactWrapper={isCompact} />
    {(label || subLabel) && (
      <LabelWrapper>
        {label && (
          <LabelText
            id={scoreLabelId}
            variant={isCompact ? 'labelXS' : 'labelS'}
          >
            {label}
          </LabelText>
        )}
        {subLabel && (
          <SubLabelText
            tag="div"
            variant={singleLineLabels ? 'bodyS' : 'bodyXS'}
            id={scoreSubLabelId}
          >
            {isCompact && value > 0 ? <>({subLabel})</> : subLabel}
          </SubLabelText>
        )}
      </LabelWrapper>
    )}
  </RatingElement>
)
